export const SUPPORTED_LANGUAGES = [
  { code: "kn", label: "ಕನ್ನಡ (Kannada)", shortLabel: "Kannada" },
  { code: "en", label: "English", shortLabel: "English" },
  { code: "hi", label: "हिन्दी (Hindi)", shortLabel: "Hindi" },
  { code: "ta", label: "தமிழ் (Tamil)", shortLabel: "Tamil" },
  { code: "te", label: "తెలుగు (Telugu)", shortLabel: "Telugu" },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export function getLanguageLabel(code?: string | null): string {
  if (!code || !code.trim()) return "Not Set";
  const found = SUPPORTED_LANGUAGES.find((l) => l.code === code.trim().toLowerCase());
  return found ? found.label : code;
}

export function getLanguageShortLabel(code?: string | null): string {
  if (!code || !code.trim()) return "Not Set";
  const found = SUPPORTED_LANGUAGES.find((l) => l.code === code.trim().toLowerCase());
  return found ? found.shortLabel : code;
}

export interface RetailerKycPhotos {
  shopPhoto?: string;
  docUrl?: string;
  languagePreference?: string;
}

/**
 * Parses the retailer photoUrl field which can contain:
 * 1. A JSON string: `{"shopPhoto":"...","docUrl":"...","languagePreference":"..."}`
 * 2. A delimited string: `shopPhoto||docUrl`
 * 3. A legacy single URL: either a PDF docUrl or an image shopPhoto
 */
export function parseRetailerKyc(photoUrl?: string | null): RetailerKycPhotos {
  if (!photoUrl || !photoUrl.trim()) return {};
  const trimmed = photoUrl.trim();

  // JSON format
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const parsed = JSON.parse(trimmed);
      return {
        shopPhoto: parsed.shopPhoto || undefined,
        docUrl: parsed.docUrl || undefined,
        languagePreference: parsed.languagePreference || undefined,
      };
    } catch {
      // Fallback to plain string handling if invalid JSON
    }
  }

  // Delimited format: shopPhoto||docUrl
  if (trimmed.includes("||")) {
    const [shopPhoto, docUrl] = trimmed.split("||");
    return {
      shopPhoto: shopPhoto?.trim() || undefined,
      docUrl: docUrl?.trim() || undefined,
    };
  }

  // Legacy single string
  const isPdf =
    trimmed.startsWith("data:application/pdf") ||
    trimmed.toLowerCase().includes(".pdf");

  if (isPdf) {
    return { docUrl: trimmed };
  }

  return { shopPhoto: trimmed };
}

/**
 * Serializes shopPhoto, docUrl, and languagePreference into a robust JSON string for backend persistence in `photoUrl`.
 */
export function serializeRetailerKyc(photos: {
  shopPhoto?: string | null;
  docUrl?: string | null;
  languagePreference?: string | null;
}): string {
  const shopPhoto = photos.shopPhoto?.trim() || "";
  const docUrl = photos.docUrl?.trim() || "";
  const languagePreference = photos.languagePreference?.trim() || "";

  if (!shopPhoto && !docUrl && !languagePreference) return "";

  return JSON.stringify({
    ...(shopPhoto ? { shopPhoto } : {}),
    ...(docUrl ? { docUrl } : {}),
    ...(languagePreference ? { languagePreference } : {}),
  });
}

/**
 * Opens a PDF document (data URI or remote HTTP URL) in a new browser tab for viewing without triggering a download.
 */
export function openPdfInNewTab(pdfUrl: string) {
  if (typeof window === "undefined" || !pdfUrl) return;

  if (pdfUrl.startsWith("http://") || pdfUrl.startsWith("https://")) {
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
    return;
  }

  try {
    const commaIdx = pdfUrl.indexOf(",");
    const base64Data = commaIdx >= 0 ? pdfUrl.slice(commaIdx + 1) : pdfUrl;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteNumbers], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  } catch {
    window.open(pdfUrl, "_blank");
  }
}

